import React from 'react';
import {Button, useDisclosure} from '@chakra-ui/react';
import {
  AnalysisRunCreditsQueryKeys,
  useGetAnalysisRunCredits,
  useLatestFieldDefinitionAnalysisRun,
  useUpdateFieldDefinitionAnalysisRun,
} from '../../../hooks/api/fieldDefinitionAnalysisRun';
import {AiFieldDefinition} from '../../../shared/enrichment';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';
import {useSelectTestListStore} from '../../Settings/TestLists/SelectTestListsModal.state';
import {useEffect, useMemo, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useIsAnalysisRunInProgress} from './hooks/useIsAnalysisRunInProgress';
import {ObjectId} from 'bson';
import {pluralize} from '../../../lib/helpers';
import {SelectTestListsModal} from '../../Settings/TestLists/SelectTestListsModal';
import _ from 'lodash';

export const SelectTestListsButton = ({
  fieldDefinition,
}: {
  fieldDefinition: AiFieldDefinition;
}) => {
  const {data: analysisRun, isPending: isAnalysisRunPending} =
    useLatestFieldDefinitionAnalysisRun({
      fieldDefinitionId: fieldDefinition.id,
    });
  const testListModal = useDisclosure();
  const updateFieldDefinitionAnalysisRun =
    useUpdateFieldDefinitionAnalysisRun();
  const isKeyplayAdmin = useIsKeyplayAdmin();
  const {testLists, setTestLists} = useSelectTestListStore();
  const analysisRunTestLists = useMemo(() => {
    return analysisRun?.results.map((result) => result.testListId) ?? [];
  }, [analysisRun]);
  const queryClient = useQueryClient();

  const {data: numCredits, isPending: isCreditsPending} =
    useGetAnalysisRunCredits({
      fieldDefinitionId: fieldDefinition.id,
      testListIds: testLists,
    });

  const isAnalysisRunInProgress = useIsAnalysisRunInProgress({
    fieldDefinitionId: fieldDefinition.id,
  });
  const samePreviewConfigs =
    !!analysisRun &&
    _.isEqual(fieldDefinition.config, analysisRun.fieldDefinition.config);

  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    // Don't override the selected test lists once set
    if (!hasInitialized && analysisRunTestLists.length) {
      setTestLists(analysisRunTestLists);
      setHasInitialized(true);
    }
  }, [analysisRunTestLists, setTestLists, hasInitialized]);

  useEffect(() => {
    if (isAnalysisRunInProgress === false) {
      queryClient.invalidateQueries({
        queryKey: AnalysisRunCreditsQueryKeys.field({
          fieldDefinitionId: fieldDefinition.id,
        }),
      });
    }
  }, [isAnalysisRunInProgress, queryClient, fieldDefinition.id]);

  const onSelectTestLists = (selectedTestLists: ObjectId[]) => {
    if (
      !selectedTestLists.length ||
      (samePreviewConfigs && _.isEqual(selectedTestLists, analysisRunTestLists))
    ) {
      return;
    }

    updateFieldDefinitionAnalysisRun.mutate({
      fieldDefinitionId: fieldDefinition.id,
      testListIds: selectedTestLists,
      bypassCreditCheck: isKeyplayAdmin,
    });
  };

  const creditsText = numCredits
    ? ` (~${pluralize(numCredits, 'credit', 'credits')})`
    : '';

  return (
    <>
      <SelectTestListsModal
        isOpen={testListModal.isOpen}
        onClose={testListModal.onClose}
        onSelectTestLists={onSelectTestLists}
        selectButtonLoading={isCreditsPending}
        selectButtonText={
          samePreviewConfigs
            ? `Update analysis run${creditsText}`
            : `Start new analysis run${creditsText}`
        }
      />
      <Button
        colorScheme="kbuttonblue"
        onClick={testListModal.onOpen}
        isDisabled={isAnalysisRunPending || isAnalysisRunInProgress}
        isLoading={updateFieldDefinitionAnalysisRun.isPending}
        variant="outline"
      >
        Select Test Lists
      </Button>
    </>
  );
};
